import { money, type SessionRequest, type StartOnlineRequest } from '../api/fee'
import type { Transaction } from '../api/types'
import { CheckoutRedirectError, failedTransactionMessage, redirectToCheckout, storedSessionId } from '../lib/checkout'
import { useMoneyAction } from '../lib/useMoneyAction'
import { ConfirmButton, Notice } from './common'

export type PayTarget =
  | { type: 'plan'; plans: string }
  | { type: 'product'; products: string }
  | { type: 'credit'; credits: string; creditCurrency: string }

/**
 * The legacy "pay" footer shared by plan-list, product-list and
 * payment-credit-view (BUSINESS-CRITICAL, RULES rule 3):
 *
 * - **current payment method** → `POST …/payment/startonline` (off-session
 *   charge of the saved card), after the legacy confirmation text;
 * - **another / new payment method** → `POST …/payment/sessions`, then a
 *   redirect to the Stripe-hosted Checkout URL the backend returned
 *   (`setupmethod` for product/credit decides whether the new card is saved;
 *   a plan always saves it, cause `setup` or `pmchange`).
 *
 * Each button is double-submit guarded (`useMoneyAction`), the startonline
 * submit carries a per-submit idempotency key, and every amount travels as a
 * string. A declined charge (transaction status 9) is shown as a failure,
 * never as success.
 */
export function PayActions({
  projectId,
  target,
  hasPaymentMethod,
  onCharged,
  disabled,
  newMethodOnly,
}: {
  projectId: string
  target: PayTarget
  hasPaymentMethod: boolean
  onCharged: (tx: Transaction | null) => void
  disabled?: boolean
  /** The legacy `app-paymentmethod-change` widget: only "pay and save a
   * new payment method" (cause `pmchange` when a card exists). */
  newMethodOnly?: boolean
}) {
  const actions = useMoneyAction()
  const busy = actions.isBusy('current') || actions.isBusy('session')

  const payCurrent = async () => {
    const body: StartOnlineRequest = { cause: 'manual', ...target }
    const tx = await actions.run('current', JSON.stringify(body), (key) => money.startOnline(projectId, body, key))
    if (tx === undefined) return
    const failure = failedTransactionMessage(tx)
    if (failure) actions.setFeedback('current', { tone: 'error', text: failure })
    else if (!tx) actions.setFeedback('current', { tone: 'error', text: 'The payment could not be started.' })
    else actions.setFeedback('current', { tone: 'success', text: 'Payment successful.' })
    onCharged(tx)
  }

  const paySession = async (saveMethod: boolean) => {
    const cause = saveMethod ? (hasPaymentMethod ? 'pmchange' : 'setup') : 'manual'
    const body: SessionRequest = { cause, ...target }
    if (target.type !== 'plan' && saveMethod) body.setupmethod = '1'
    const old = storedSessionId(projectId)
    if (old) body.oldSessionId = old
    await actions.run('session', JSON.stringify(body), async () => {
      const session = await money.createSession(projectId, body)
      try {
        redirectToCheckout(projectId, session)
      } catch (err) {
        if (err instanceof CheckoutRedirectError) {
          actions.setFeedback('session', { tone: 'error', text: err.message })
          return session
        }
        throw err
      }
      actions.setFeedback('session', { tone: 'success', text: 'Opening the Stripe payment page…' })
      return session
    })
  }

  return (
    <div className="fs-pay-actions">
      <div className="portal-inline-actions">
        {newMethodOnly ? (
          <button type="button" disabled={disabled || busy} onClick={() => void paySession(true)}>
            {actions.isBusy('session') ? 'Opening payment page…' : 'Pay and save new payment method'}
          </button>
        ) : hasPaymentMethod ? (
          <>
            <ConfirmButton
              label="Pay with current payment method"
              pendingText="Are you sure you want to start this transaction?"
              busy={actions.isBusy('current')}
              disabled={disabled || busy}
              onConfirm={() => void payCurrent()}
            />
            {target.type !== 'plan' && (
              <button type="button" disabled={disabled || busy} onClick={() => void paySession(false)}>
                Pay with another payment method (don&apos;t change current payment method)
              </button>
            )}
            <button type="button" disabled={disabled || busy} onClick={() => void paySession(true)}>
              Pay with and save new payment method
            </button>
          </>
        ) : (
          <button type="button" disabled={disabled || busy} onClick={() => void paySession(true)}>
            {actions.isBusy('session') ? 'Opening payment page…' : 'Pay with new payment method'}
          </button>
        )}
      </div>
      <Notice feedback={actions.feedbackFor('current')} />
      <Notice feedback={actions.feedbackFor('session')} />
    </div>
  )
}
